import { FrustumIntersector } from './FrustumIntersector';
import { LmvBox3 as Box3 } from './LmvBox3';

const MAX_INSTANCES_PER_SCENE = 500;

const _tmpBox = new Box3();

/** @import {RenderModel} from './RenderModel'; */

/**
 * Linear scene iterator for 3D models with strict opaque/transparent phase separation.
 *
 * Unlike ModelIteratorLinear, this iterator is mutation-safe and whose instances are added, removed and
 * re-materialized programmatically in arbitrary order.
 * It also works for normally loaded 3D models during their pre-BVH phase.
 *
 * Structure:
 *  - Instances are grouped into regions of _instancesPerBatch by id. Each region has one opaque
 *    RenderBatch (_opaqueBatches[sceneIndex]) and, on demand, one transparent RenderBatch
 *    (_transparentBatches[sceneIndex]). Opaque batches render first (sortObjects=false), transparent
 *    batches afterwards (sortObjects=true), back-to-front sorted each frame if WBOIT is not used.
 *  - Each batch keeps count === lastItem === number of live instances it holds, with indices densely
 *    packed in [0, lastItem). RenderBatch.sortByDepth() relies on count, so this keeps transparent
 *    depth sorting correct after arbitrary add/remove.
 *  - _instanceLoc maps instanceId -> location for O(1) removal/toggle/lookup. It encodes both the group and
 *    the slot within the batch's indices: -1 = absent, value >= 0 = opaque at slot=value,
 *    value <= -2 = transparent at slot=-(value)-2.
 *
 * Classification happens in addInstance() (after setMesh has set the material). The material-changed
 * callback handles runtime opaque<->transparent toggles, and the instances-removed callback handles removals.
 */
export class ModelIteratorLinear3D {

  /**
   * @param {RenderModel} renderModel - The render model that owns the shared instance store. Used to
   *   access its instance store, RenderBatch constructor and to register the material-changed and
   *   instances-removed callbacks that keep this iterator in sync with mutations.
   */
  constructor(renderModel) {
    this._instances = renderModel.getInstances();
    this._RenderBatch = renderModel.RenderBatch;
    this._model = renderModel;

    this._instancesPerBatch = MAX_INSTANCES_PER_SCENE;

    this._opaqueBatches = []; // opaque batches, one per region (may contain holes)
    this._transparentBatches = []; // transparent batches (sparse, created on demand)
    this._transparentCount = 0; // number of transparent instances; drives the transparent phase
    this._opaquePhase = true;
    this._currentScene = 0;

    // WBOIT: when false, the combined pass (after reset()) emits opaque batches only.
    // transparents are left to the correction pass via resetTransparents().
    this._drawTransparents = true;

    // instanceId -> packed location (group + slot); -1 = absent. See class doc for the encoding.
    this._instanceLoc = new Int32Array(0);

    // Cached combined batch list for getGeomScenes(); rebuilt only when the batch set changes.
    this._allScenes = null;
    this._allScenesDirty = true;

    this._setInstanceOrder(this._instances.getCount());

    this._onMaterialChanged = (instanceId, material) => this._handleMaterialChanged(instanceId, material);
    this._instances.registerMaterialChangedCallback(this._onMaterialChanged);


    this._onInstancesRemoved = (instanceIds) => this._handleInstancesRemoved(instanceIds);
    this._instances.registerInstancesRemovedCallback(this._onInstancesRemoved);
  }

  /**
   * Detaches the iterator from the instance store by removing its material-changed and
   * instances-removed callbacks, then drops references to the model and store.
   *
   * NOTE: the batch arrays (_opaqueBatches/_transparentBatches) are intentionally kept alive.
   * When the linear iterator is replaced by the BVH, consumers (e.g. ModelUniformUpdater.freeAllUniforms)
   * still read getGeomScenes() on the dtor'd iterator to release per-batch GPU resources.
   */
  dtor() {
    if (this._onMaterialChanged) {
      this._instances?.removeMaterialChangedCallback(this._onMaterialChanged);
      this._onMaterialChanged = null;
    }
    if (this._onInstancesRemoved) {
      this._instances?.removeInstancesRemovedCallback?.(this._onInstancesRemoved);
      this._onInstancesRemoved = null;
    }
    this._instances = null;
    this._RenderBatch = null;
    this._model = null;
  }

  /**
   * Pre-create empty opaque batches per region when the instance count is known in advance
   * (loaded models), to avoid repeated lazy allocation. Batches start empty (count = 0) and grow
   * as instances are activated. For dynamically grown stores (count unknown) batches are created
   * lazily in addInstance().
   *
   * @param {number} instanceCount - The known number of instances. When falsy, nothing is pre-allocated.
   */
  _setInstanceOrder(instanceCount) {
    if (!instanceCount) {
      return;
    }
    // NOTE: regions are keyed by id (getSceneIndex = floor(id / _instancesPerBatch)), but here we
    // size by count. This is exact only when ids are dense in [0, instanceCount) — which holds for
    // loaded models (the only case that reaches here with a non-zero count; dynamic stores construct
    // empty and grow lazily in _appendToOpaque). For sparse ids the per-region capacity is just a
    // hint: batches grow on demand in _append(), so it only affects initial allocation, never
    // correctness. Clamp to [1, _instancesPerBatch] so a sparse/short last region can't go non-positive.
    const numScenes = Math.ceil(instanceCount / this._instancesPerBatch);
    this._opaqueBatches.length = numScenes;
    for (let i = 0; i < numScenes; i++) {
      const start = i * this._instancesPerBatch;
      const capacity = Math.max(1, Math.min(this._instancesPerBatch, instanceCount - start));
      this._opaqueBatches[i] = this._createBatch(capacity, /*sortObjects=*/false, /*transparent=*/false);
    }
    this._allScenesDirty = true;
  }

  /**
   * Adds an instance to the iterator, classifying it into the opaque or transparent batch of its
   * region based on its current material. Idempotent: re-adding an instance that is already tracked
   * is a no-op (guards against id reuse where a removed id is recycled by a later add()).
   *
   * @param {number} instance - The instance id to add.
   */
  addInstance(instance) {
    if (this._isPresent(instance)) {
      return;
    }
    const sceneIndex = this.getSceneIndex(instance);
    if (this._instances.getMaterial(instance)?.transparent) {
      this._appendToTransparent(sceneIndex, instance);
    } else {
      this._appendToOpaque(sceneIndex, instance);
    }
  }

  // ── Iterator interface ────────────────────────────────────────────────────

  /**
   * Restarts traversal at the beginning of the opaque phase. Called once per frame before iterating.
   *
   * Also performs a best-effort opaque material sort: re-sorts an opaque batch only once it has
   * settled, i.e. its membership changed but it was not edited during the frame that just finished.
   * Resets per-batch visibility status when a resetVisStatus() was requested.
   */
  reset() {
    this._currentScene = 0;
    this._opaquePhase = true;

    const resetVis = this._resetVisStatus;

    // Best-effort opaque material sort: re-sort an opaque batch only once it has settled, i.e.
    // its membership changed but it was not edited during the frame that just finished.
    for (const b of this._opaqueBatches) {
      if (!b) continue;
      if (resetVis) b.resetVisStatus?.();
      if (b._iterMaterialDirty && !b._iterEdited) {
        b.sortDone = false;
        b.numAdded = b.count; // satisfy RenderBatch.sortByMaterial()'s completeness guard
        b._iterMaterialDirty = false;
      }
      b._iterEdited = false;
    }

    if (resetVis) {
      for (const b of this._transparentBatches) b?.resetVisStatus?.();
      this._resetVisStatus = false;
    }
  }

  /**
   * @returns {boolean} True when traversal is exhausted for the current pass. While in the opaque
   *   phase, returns true once all opaque batches are consumed and this pass owes no transparent
   *   phase (i.e. _drawTransparents is false or there are no transparent instances). In the
   *   transparent phase, returns true once all transparent batches are consumed.
   */
  done() {
    if (this._opaquePhase) {
      if (this._currentScene < this._opaqueBatches.length) return false;
      // Opaques exhausted: done unless this pass still owes a transparent phase. When _drawTransparents
      // is false (WBOIT correction-pass deferral) the combined pass ends here, matching ModelIteratorBVH.
      return !this._drawTransparents || this._transparentCount === 0;
    }
    return this._currentScene >= this._transparentBatches.length;
  }

  /**
   * Advances traversal and returns the next non-empty batch to render. Opaque batches are emitted
   * first (sortObjects=false), then transparent batches (sortObjects=true). Empty batches are skipped.
   * The opaque->transparent phase transition only happens when this pass draws transparents; with
   * WBOIT the combined pass may defer them (_drawTransparents === false) to the correction pass
   * entered via resetTransparents().
   *
   * @returns {RenderBatch|null} The next batch to render, or null when traversal is complete.
   */
  nextBatch() {
    if (this._opaquePhase) {
      while (this._currentScene < this._opaqueBatches.length) {
        const batch = this._opaqueBatches[this._currentScene++];
        if (batch && batch.lastItem > 0) {
          batch.renderImportance = 0;
          batch.sortObjects = false;
          return batch;
        }
      }
      // Enter the transparent phase only if this pass draws transparents. With WBOIT, the combined pass
      // may defer them (_drawTransparents === false) to the correction pass entered via resetTransparents().
      if (this._drawTransparents && this._transparentCount > 0) {
        this._opaquePhase = false;
        this._currentScene = 0;
      } else {
        return null;
      }
    }

    while (this._currentScene < this._transparentBatches.length) {
      const batch = this._transparentBatches[this._currentScene++];
      if (batch && batch.lastItem > 0) {
        batch.renderImportance = 0;
        batch.sortObjects = true;
        return batch;
      }
    }

    return null;
  }

  /**
   * WBOIT only: controls whether the next combined pass traverses transparent batches.
   *
   * When false, the next combined pass (after reset()) emits opaque batches only and the
   * transparent phase is deferred to the correction pass via resetTransparents(). When true (the
   * default), reset() traverses opaques then transparents as usual.
   *
   * @param {boolean} drawTransparents - Whether the next pass should draw transparent batches.
   */
  setDrawTransparents(drawTransparents) {
    this._drawTransparents = drawTransparents;
  }

  /**
   * Restarts traversal at the transparent phase only (used for a WBOIT accumulation pass).
   * Independent of _drawTransparents: the correction pass always re-traverses transparents.
   */
  resetTransparents() {
    this._opaquePhase = false;
    this._currentScene = 0;
  }

  /**
   * Requests that per-batch visibility status be reset on the next reset(). Deferred so it applies
   * uniformly across opaque and transparent batches at the start of the next frame.
   */
  resetVisStatus() {this._resetVisStatus = true;}

  /**
   * Returns all batches (opaque + transparent) for consumers like ground shadows/reflections and
   * the WebGPU uniform/bundle updaters. The result is cached and only rebuilt when the set changes,
   * so callers that detect scene changes by array identity keep working.
   *
   * @returns {RenderBatch[]} The combined list of live opaque and transparent batches.
   */
  getGeomScenes() {
    if (this._allScenesDirty || !this._allScenes) {
      const all = [];
      for (const b of this._opaqueBatches) if (b) all.push(b);
      for (const b of this._transparentBatches) if (b) all.push(b);
      this._allScenes = all;
      this._allScenesDirty = false;
    }
    return this._allScenes;
  }

  /** @returns {number} The number of live batches (opaque + transparent). */
  getSceneCount() {return this.getGeomScenes().length;}

  /** @returns {null} Always null; batches own their indices, there is no shared flat instance-order array. */
  getInstanceOrder() {return null;}

  /**
   * @param {number} instanceId - The instance id to map to a region.
   * @returns {number} The region (scene) index that owns the instance: floor(id / _instancesPerBatch).
   */
  getSceneIndex(instanceId) {return Math.floor(instanceId / this._instancesPerBatch);}

  /**
   * @param {number} instanceId - The instance id to look up.
   * @returns {RenderBatch|undefined} The batch (opaque or transparent) that currently holds the
   *   instance, or undefined if the instance is not tracked.
   */
  getInstanceBatch(instanceId) {
    if (!this._isPresent(instanceId)) return undefined;
    const sceneIndex = this.getSceneIndex(instanceId);
    return this._instanceLoc[instanceId] < 0 ? this._transparentBatches[sceneIndex] : this._opaqueBatches[sceneIndex];
  }

  // ── Spatial queries ───────────────────────────────────────────────────────

  /**
   * Recomputes per-batch bounding boxes across all opaque and transparent batches.
   *
   * @param {*} _unused - Unused; present for interface parity with other iterators.
   * @param {boolean} [forceUpdate=true] - When false, recomputes only batches whose bbox was
   *   invalidated (e.g. by a removal), leaving incrementally maintained batches (grown via
   *   addToBox) untouched.
   */
  updateBounds(_unused, forceUpdate = true) {
    for (const b of this._opaqueBatches) b?.updateBounds(forceUpdate);
    for (const b of this._transparentBatches) b?.updateBounds(forceUpdate);
  }

  /**
   * Accumulates the visible and hidden bounding boxes across all batches.
   *
   * @param {Box3} visibleBounds - Box to union the visible bounds of every batch into (mutated).
   * @param {Box3} hiddenBounds - Box to union the hidden bounds of every batch into (mutated).
   */
  getBounds(visibleBounds, hiddenBounds) {
    const accumulate = (b) => {
      if (!b) return;
      visibleBounds.union(b.getBoundingBox());
      hiddenBounds.union(b.getBoundingBoxHidden());
    };
    for (const b of this._opaqueBatches) accumulate(b);
    for (const b of this._transparentBatches) accumulate(b);
  }

  /**
   * Ray-casts against every batch, collecting hits.
   *
   * @param {THREE.Raycaster} raycaster - The ray to test against.
   * @param {Array} intersects - Output array that receives intersection results (mutated).
   * @param {*} [dbIdFilter] - Optional dbId filter restricting which instances may be hit.
   * @param {*} [options] - Optional raycast options forwarded to each batch.
   */
  rayCast(raycaster, intersects, dbIdFilter, options) {
    for (const b of this._opaqueBatches) b?.raycast(raycaster, intersects, dbIdFilter, options);
    for (const b of this._transparentBatches) b?.raycast(raycaster, intersects, dbIdFilter, options);
  }

  /**
   * Tests every batch against a frustum and reports the instances inside it. Batches whose bbox is
   * fully outside the frustum are skipped; batches fully contained pass containsAll=true so their
   * instances can be reported without per-instance testing.
   *
   * @param {FrustumIntersector} frustumIntersector - The frustum to test against.
   * @param {Function} callback - Invoked for instances inside the frustum.
   */
  intersectFrustum(frustumIntersector, callback) {
    const test = (b) => {
      if (!b) return;
      const res = frustumIntersector.intersectsBox(b.getBoundingBox());
      if (res !== FrustumIntersector.OUTSIDE) {
        b.intersectFrustum(frustumIntersector, callback, res === FrustumIntersector.CONTAINS);
      }
    };
    for (const b of this._opaqueBatches) test(b);
    for (const b of this._transparentBatches) test(b);
  }

  /**
   * Creates a deep copy of this iterator's batches and location map.
   *
   * NOTE: unlike ModelIteratorLinear.clone() (which yields an essentially static copy), the clone
   * returned here is LIVE-coupled to the shared instance store: the constructor registers its own
   * material-changed / instances-removed callbacks, so the clone keeps mirroring add/remove/material
   * mutations independently of this iterator. Callers MUST dtor() the clone when done; otherwise its
   * callbacks leak on the store and keep mutating a now-orphaned state map.
   *
   * @returns {ModelIteratorLinear3D} A live-coupled clone of this iterator.
   */
  clone() {
    const result = new ModelIteratorLinear3D(this._model);
    // The constructor already pre-created fresh opaque batches and registered callbacks.
    // Replace the batches and location map with deep copies that mirror this iterator's state.
    result._opaqueBatches = this._opaqueBatches.map((b) => this._cloneBatch(result, b, /*transparent=*/false));
    result._transparentBatches = this._transparentBatches.map((b) => this._cloneBatch(result, b, /*transparent=*/true));
    result._instanceLoc = this._instanceLoc.slice();
    result._transparentCount = this._transparentCount;
    result._allScenesDirty = true;
    return result;
  }

  // ── Batch helpers ─────────────────────────────────────────────────────────

  /**
   * Creates an empty RenderBatch with a pre-sized index buffer. Registers a fragOrderChanged
   * callback so that in-place reorders of batch.indices (RenderBatch.sortByDepth()/sortByMaterial())
   * keep _instanceLoc's slots in sync — otherwise a later O(1) swap-removal would read a stale slot.
   *
   * @param {number} capacity - Initial capacity of the batch's index buffer (clamped to >= 1).
   * @param {boolean} sortObjects - Whether the batch should depth-sort its objects each frame
   *   (true for transparent batches, false for opaque).
   * @param {boolean} transparent - Whether this batch is the transparent batch of its region
   *   (affects how _instanceLoc encodes the slot).
   * @returns {RenderBatch} The newly created empty batch.
   */
  _createBatch(capacity, sortObjects, transparent) {
    const batch = new this._RenderBatch(this, this._instances, 0, 0); // count grows with lastItem
    batch.indices = new Uint32Array(Math.max(capacity, 1));
    batch.lastItem = 0;
    batch.sortObjects = sortObjects;
    batch.registerFragOrderChangedCallback((start, length) => this._syncLocForRange(batch, transparent, start, length));
    return batch;
  }

  /**
   * fragOrderChanged callback: re-reads a batch's indices over the reordered range and updates
   * _instanceLoc so slots stay correct after an in-place reorder (sortByDepth()/sortByMaterial()).
   *
   * @param {RenderBatch} batch - The batch whose indices were reordered.
   * @param {boolean} transparent - Whether this is a transparent batch (affects the encoded location).
   * @param {number} start - The start of the reordered range (batch.start is always 0, so this is a slot index).
   * @param {number} length - The length of the reordered range.
   */
  _syncLocForRange(batch, transparent, start, length) {
    const end = Math.min(start + length, batch.lastItem);
    for (let i = start; i < end; i++) {
      this._setLoc(batch.indices[i], transparent, i);
    }
  }

  /**
   * Deep-copies a single batch, including its indices and bookkeeping, for clone().
   *
   * @param {ModelIteratorLinear3D} owner - The iterator that will own the cloned batch.
   * @param {RenderBatch|undefined} src - The source batch to copy, or a falsy value (passed through).
   * @param {boolean} transparent - Whether src is a transparent batch (affects the encoded location
   *   used by owner's fragOrderChanged sync callback).
   * @returns {RenderBatch|undefined} The cloned batch, or src unchanged when falsy.
   */
  _cloneBatch(owner, src, transparent) {
    if (!src) return src;
    const b = new this._RenderBatch(owner, this._instances, src.start, src.count, src.indices ? src.indices.slice() : undefined);
    b.lastItem = src.lastItem;
    b.numAdded = src.numAdded;
    b.numAddedTotal = src.numAddedTotal;
    b.sortDone = src.sortDone;
    b.sortObjects = src.sortObjects;
    b.bboxes = src.bboxes.slice();
    b.registerFragOrderChangedCallback((start, length) => owner._syncLocForRange(b, transparent, start, length));
    return b;
  }

  /**
   * Appends an instance to the opaque batch of a region, creating the batch on demand, and marks
   * the batch's opaque material order as dirty.
   *
   * @param {number} sceneIndex - The region index (see getSceneIndex).
   * @param {number} instanceId - The instance id to append.
   */
  _appendToOpaque(sceneIndex, instanceId) {
    let batch = this._opaqueBatches[sceneIndex];
    if (!batch) {
      batch = this._opaqueBatches[sceneIndex] = this._createBatch(this._instancesPerBatch, /*sortObjects=*/false, /*transparent=*/false);
      this._allScenesDirty = true;
    }
    this._append(batch, instanceId, /*transparent=*/false);
    this._markOpaqueChanged(batch);
  }

  /**
   * Appends an instance to the transparent batch of a region, creating the batch on demand, and
   * bumps the transparent instance count that drives the transparent phase.
   *
   * @param {number} sceneIndex - The region index (see getSceneIndex).
   * @param {number} instanceId - The instance id to append.
   */
  _appendToTransparent(sceneIndex, instanceId) {
    let batch = this._transparentBatches[sceneIndex];
    if (!batch) {
      batch = this._transparentBatches[sceneIndex] = this._createBatch(this._instancesPerBatch, /*sortObjects=*/true, /*transparent=*/true);
      this._allScenesDirty = true;
    }
    this._append(batch, instanceId, /*transparent=*/true);
    this._transparentCount++;
  }

  /**
   * Appends instanceId to a batch, keeping count === lastItem and updating the location map and
   * bounds. Grows the batch's index buffer if needed and incrementally extends its bbox.
   *
   * @param {RenderBatch} batch - The batch to append to.
   * @param {number} instanceId - The instance id to append.
   * @param {boolean} transparent - Whether this is a transparent batch (affects the encoded location).
   */
  _append(batch, instanceId, transparent) {
    const n = batch.lastItem;
    if (n >= batch.indices.length) {
      const grown = new Uint32Array(Math.max(batch.indices.length * 2, n + 1));
      grown.set(batch.indices);
      batch.indices = grown;
    }
    batch.indices[n] = instanceId;
    batch.lastItem = n + 1;
    batch.count = n + 1;

    // Incremental bbox + bookkeeping (mirrors RenderBatch.onFragmentAdded, but without its
    // instanceId-based lastItem assumption.
    this._instances.getWorldBounds(instanceId, _tmpBox);
    batch.addToBox(_tmpBox, false);
    batch.sortDone = false;
    batch.numAdded++;
    batch.numAddedTotal++;
    batch.isComplete = false;
    batch.useRenderBundles = false;

    this._setLoc(instanceId, transparent, n);
  }

  /**
   * O(1) swap-removal: moves the last element into the removed slot and fixes its location entry.
   * Does NOT clear the removed instanceId's location entry; the caller either clears it (removal) or
   * re-assigns it via a subsequent _append (opaque<->transparent move). Invalidates the batch bbox
   * since a summed bbox cannot be reduced incrementally.
   *
   * @param {RenderBatch} batch - The batch to remove from.
   * @param {number} instanceId - The instance id to remove.
   * @param {boolean} transparent - Whether this is a transparent batch (affects slot decoding).
   */
  _swapRemove(batch, instanceId, transparent) {
    const val = this._instanceLoc[instanceId];
    const slot = transparent ? -val - 2 : val;
    const last = batch.lastItem - 1;

    const movedId = batch.indices[last];
    batch.indices[slot] = movedId;
    if (movedId !== instanceId) {
      this._setLoc(movedId, transparent, slot);
    }

    batch.lastItem = last;
    batch.count = last;
    batch.numAdded = Math.max(0, batch.numAdded - 1);
    batch.numAddedTotal = Math.max(0, batch.numAddedTotal - 1);
    batch.sortDone = false;
    batch.isComplete = false;
    batch.useRenderBundles = false;
    batch.invalidateBBox?.(); // summed bbox cannot be reduced incrementally; recomputed on updateBounds()
  }

  /**
   * Instances-removed callback: removes the given instances from their batches and clears their
   * location entries, updating the transparent count or opaque dirty flag accordingly.
   *
   * @param {number|number[]} instanceIds - A single instance id or an array of instance ids to remove.
   */
  _handleInstancesRemoved(instanceIds) {
    const ids = Array.isArray(instanceIds) ? instanceIds : [instanceIds];
    for (const instanceId of ids) {
      if (!this._isPresent(instanceId)) continue;

      const sceneIndex = this.getSceneIndex(instanceId);
      const transparent = this._instanceLoc[instanceId] < 0;
      const batch = transparent ? this._transparentBatches[sceneIndex] : this._opaqueBatches[sceneIndex];
      if (!batch) {
        this._clearLoc(instanceId);
        continue;
      }

      this._swapRemove(batch, instanceId, transparent);
      this._clearLoc(instanceId);
      if (transparent) {
        this._transparentCount--;
      } else {
        this._markOpaqueChanged(batch);
      }
    }
  }

  /**
   * Material-changed callback: handles runtime opaque<->transparent transitions by moving the
   * instance between the opaque and transparent batches of its region when its transparency flips.
   * Keys off the tracked location (not fromLoader): an instance that is not yet tracked (e.g. the
   * material callback fired by setMesh during add(), before addInstance) is ignored here and
   * classified by addInstance().
   *
   * @param {number} instanceId - The instance whose material changed.
   * @param {THREE.Material} material - The instance's new material.
   */
  _handleMaterialChanged(instanceId, material) {
    if (!this._isPresent(instanceId)) return;

    const sceneIndex = this.getSceneIndex(instanceId);
    const wasTransparent = this._instanceLoc[instanceId] < 0;
    const isNowTransparent = !!material?.transparent;

    if (wasTransparent === isNowTransparent) {
      // Same group; the material id may have changed, so the opaque material order may be stale.
      if (!isNowTransparent) this._markOpaqueChanged(this._opaqueBatches[sceneIndex]);
      return;
    }

    if (isNowTransparent) {
      const opaque = this._opaqueBatches[sceneIndex];
      this._swapRemove(opaque, instanceId, /*transparent=*/false);
      this._appendToTransparent(sceneIndex, instanceId);
      this._markOpaqueChanged(opaque);
    } else {
      const transparent = this._transparentBatches[sceneIndex];
      this._swapRemove(transparent, instanceId, /*transparent=*/true);
      this._transparentCount--;
      this._appendToOpaque(sceneIndex, instanceId);
    }
  }

  /**
   * Flags an opaque batch as having changed membership this frame so reset() can re-sort it by
   * material once it has settled.
   *
   * @param {RenderBatch|undefined} batch - The opaque batch to flag; no-op when falsy.
   */
  _markOpaqueChanged(batch) {
    if (!batch) return;
    batch._iterMaterialDirty = true;
    batch._iterEdited = true;
  }

  // ── Instance location map ──────────────────────────────────────────────────

  /**
   * @param {number} instanceId - The instance id to check.
   * @returns {boolean} True if the instance is currently tracked (has a non-absent location entry).
   */
  _isPresent(instanceId) {
    return instanceId >= 0 && instanceId < this._instanceLoc.length && this._instanceLoc[instanceId] !== -1;
  }

  /**
   * Records an instance's packed location (group + slot) in the location map, growing it if needed.
   * Encoding: opaque slot=value (>= 0), transparent value=-(slot)-2 (<= -2). See class doc.
   *
   * @param {number} instanceId - The instance id whose location is being set.
   * @param {boolean} transparent - Whether the instance lives in a transparent batch.
   * @param {number} slot - The instance's slot index within the batch's indices.
   */
  _setLoc(instanceId, transparent, slot) {
    this._ensureLocCapacity(instanceId);
    this._instanceLoc[instanceId] = transparent ? -(slot + 2) : slot;
  }

  /**
   * Marks an instance as absent (-1) in the location map.
   *
   * @param {number} instanceId - The instance id to clear.
   */
  _clearLoc(instanceId) {
    if (instanceId < this._instanceLoc.length) {
      this._instanceLoc[instanceId] = -1;
    }
  }

  /**
   * Grows the location map (doubling capacity) so it can hold an entry for the given instance id,
   * filling new slots with -1 (absent).
   *
   * @param {number} instanceId - The instance id that must fit in the location map.
   */
  _ensureLocCapacity(instanceId) {
    if (instanceId < this._instanceLoc.length) return;
    let n = this._instanceLoc.length || 16;
    while (n <= instanceId) n *= 2;
    const grown = new Int32Array(n).fill(-1);
    grown.set(this._instanceLoc);
    this._instanceLoc = grown;
  }
}